import React, { useEffect, useMemo, useState } from 'react';
import { FileText, Plus, Save, Trash2 } from 'lucide-react';
import {
  contentApi,
  ContentPage,
  ContentPageKey,
  ContentPageSection,
} from '../../api/content';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Spinner } from '../../components/ui/Spinner';
import { usePage } from '../../hooks/usePage';
import { useToast } from '../../hooks/useToast';

type Draft = {
  title: string;
  isPublished: boolean;
  effectiveDate: string;
  sections: ContentPageSection[];
};

const pageLabels: Record<ContentPageKey, string> = {
  shop_faqs: 'Shop FAQs',
  referral_faqs: 'Referral FAQs',
  shop_terms: 'Shop Terms',
  returns_refund_policy: 'Returns & Refund Policy',
  general_terms: 'General Terms',
  privacy_policy: 'Privacy Policy',
  legal: 'Legal',
  escrow_policy: 'Escrow Policy',
};

function makeDraft(page: ContentPage): Draft {
  return {
    title: page.title,
    isPublished: page.isPublished,
    effectiveDate: page.effectiveDate ? page.effectiveDate.slice(0, 10) : '',
    sections: page.sections.map((section, index) => ({
      ...section,
      sortOrder: section.sortOrder ?? index,
    })),
  };
}

function emptySection(type: ContentPage['contentType'], index: number): ContentPageSection {
  return type === 'faq'
    ? {
        id: `faq-${Date.now()}-${index}`,
        question: '',
        answer: '',
        sortOrder: index,
      }
    : {
        id: `section-${Date.now()}-${index}`,
        title: '',
        description: '',
        sortOrder: index,
      };
}

export const ContentPages: React.FC = () => {
  usePage('Content');
  const toast = useToast();
  const [pages, setPages] = useState<ContentPage[]>([]);
  const [selectedKey, setSelectedKey] = useState<ContentPageKey>('shop_faqs');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const selected = useMemo(
    () => pages.find((page) => page.key === selectedKey) ?? null,
    [pages, selectedKey],
  );

  async function fetchPages() {
    try {
      setLoading(true);
      const data = await contentApi.listPages();
      setPages(data);
      const next = data.find((page) => page.key === selectedKey) ?? data[0];
      if (next) {
        setSelectedKey(next.key);
        setDraft(makeDraft(next));
      }
    } catch (err) {
      toast.error(err, 'Failed to load content pages.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void fetchPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function selectPage(page: ContentPage) {
    setSelectedKey(page.key);
    setDraft(makeDraft(page));
  }

  function updateSection(index: number, patch: Partial<ContentPageSection>) {
    setDraft((current) => {
      if (!current) return current;
      return {
        ...current,
        sections: current.sections.map((section, i) =>
          i === index ? { ...section, ...patch } : section,
        ),
      };
    });
  }

  function addSection() {
    if (!selected) return;
    setDraft((current) => {
      if (!current) return current;
      return {
        ...current,
        sections: [
          ...current.sections,
          emptySection(selected.contentType, current.sections.length),
        ],
      };
    });
  }

  function removeSection(index: number) {
    setDraft((current) => {
      if (!current) return current;
      return {
        ...current,
        sections: current.sections.filter((_, i) => i !== index),
      };
    });
  }

  async function savePage() {
    if (!selected || !draft) return;
    try {
      setSaving(true);
      const saved = await contentApi.updatePage(selected.key, {
        title: draft.title,
        isPublished: draft.isPublished,
        effectiveDate: draft.effectiveDate || null,
        sections: draft.sections.map((section, index) => ({
          ...section,
          sortOrder: index,
        })),
      });
      setPages((current) =>
        current.map((page) => (page.key === saved.key ? saved : page)),
      );
      setDraft(makeDraft(saved));
      toast.success(`${saved.title} updated.`);
    } catch (err) {
      toast.error(err, 'Failed to save content page.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spinner size={28} />
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 24 }}>
      <Card padding={0}>
        <div style={{ padding: 16, borderBottom: '1px solid var(--neutral-300)' }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--neutral-800)' }}>
            Managed Pages
          </h3>
          <p style={{ marginTop: 4, fontSize: 12, color: 'var(--neutral-500)' }}>
            Content shown in mobile settings.
          </p>
        </div>
        <div style={{ padding: 8, display: 'grid', gap: 6 }}>
          {pages.map((page) => {
            const active = page.key === selectedKey;
            return (
              <button
                key={page.key}
                onClick={() => selectPage(page)}
                style={{
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  padding: '12px 10px',
                  background: active ? 'var(--blue-100)' : 'transparent',
                  color: active ? 'var(--blue-800)' : 'var(--neutral-700)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <FileText size={16} />
                <span style={{ fontWeight: 600 }}>{pageLabels[page.key]}</span>
              </button>
            );
          })}
        </div>
      </Card>

      {selected && draft ? (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
            <div>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--neutral-800)' }}>
                {pageLabels[selected.key]}
              </h2>
              <p style={{ marginTop: 4, fontSize: 13, color: 'var(--neutral-500)' }}>
                Version {selected.version} · Updated {new Date(selected.updatedAt).toLocaleString('en-NG')}
              </p>
            </div>
            <Button onClick={savePage} loading={saving}>
              <Save size={16} />
              Save
            </Button>
          </div>

          <div style={{ marginTop: 24, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))', gap: 16 }}>
            <Field label="Page title">
              <input
                value={draft.title}
                onChange={(e) => setDraft((current) => current ? { ...current, title: e.target.value } : current)}
                style={inputStyle}
              />
            </Field>
            <Field label="Effective date">
              <input
                type="date"
                value={draft.effectiveDate}
                onChange={(e) => setDraft((current) => current ? { ...current, effectiveDate: e.target.value } : current)}
                style={inputStyle}
              />
            </Field>
            <label style={{ display: 'flex', alignItems: 'end', gap: 8, paddingBottom: 9 }}>
              <input
                type="checkbox"
                checked={draft.isPublished}
                onChange={(e) => setDraft((current) => current ? { ...current, isPublished: e.target.checked } : current)}
              />
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--neutral-700)' }}>
                Published
              </span>
            </label>
          </div>

          <div style={{ marginTop: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--neutral-800)' }}>
              {selected.contentType === 'faq' ? 'FAQs' : 'Sections'}
            </h3>
            <Button variant="secondary" size="sm" onClick={addSection}>
              <Plus size={14} />
              Add
            </Button>
          </div>

          <div style={{ marginTop: 14, display: 'grid', gap: 14 }}>
            {draft.sections.map((section, index) => (
              <ContentRow
                key={`${section.id}-${index}`}
                type={selected.contentType}
                index={index}
                section={section}
                onChange={(patch) => updateSection(index, patch)}
                onRemove={() => removeSection(index)}
              />
            ))}
          </div>
        </Card>
      ) : null}
    </div>
  );
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'grid', gap: 6 }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--neutral-600)' }}>
        {label}
      </span>
      {children}
    </label>
  );
}

function ContentRow({
  type,
  index,
  section,
  onChange,
  onRemove,
}: {
  type: ContentPage['contentType'];
  index: number;
  section: ContentPageSection;
  onChange: (patch: Partial<ContentPageSection>) => void;
  onRemove: () => void;
}) {
  return (
    <div
      style={{
        border: '1px solid var(--neutral-300)',
        borderRadius: 'var(--radius-lg)',
        padding: 16,
        display: 'grid',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--neutral-500)' }}>
          #{index + 1}
        </span>
        <Button variant="ghost" size="sm" onClick={onRemove}>
          <Trash2 size={14} />
          Remove
        </Button>
      </div>

      {type === 'faq' ? (
        <>
          <Field label="Question">
            <input
              value={section.question ?? ''}
              onChange={(e) => onChange({ question: e.target.value })}
              style={inputStyle}
            />
          </Field>
          <Field label="Answer">
            <textarea
              value={section.answer ?? ''}
              onChange={(e) => onChange({ answer: e.target.value })}
              rows={4}
              style={textAreaStyle}
            />
          </Field>
        </>
      ) : (
        <>
          <Field label="Title">
            <input
              value={section.title ?? ''}
              onChange={(e) => onChange({ title: e.target.value })}
              style={inputStyle}
            />
          </Field>
          <Field label="Description">
            <textarea
              value={section.description ?? ''}
              onChange={(e) => onChange({ description: e.target.value })}
              rows={4}
              style={textAreaStyle}
            />
          </Field>
        </>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  height: 40,
  border: '1px solid var(--neutral-300)',
  borderRadius: 'var(--radius-md)',
  padding: '0 12px',
  fontFamily: 'var(--font-family)',
  color: 'var(--neutral-800)',
  background: 'var(--neutral-100)',
};

const textAreaStyle: React.CSSProperties = {
  ...inputStyle,
  minHeight: 104,
  height: 'auto',
  resize: 'vertical',
  padding: 12,
  lineHeight: 1.5,
};
